import { useMemo } from 'react'
import PropTypes from 'prop-types'
import hljs from 'highlight.js'

// The highlighted `code` of a fenced block. The `pre` around it is react-markdown's
// own, overridden in ChatMessage to carry the scroll box — this used to render a
// second `pre` of its own inside that one, which is where the doubled border and
// padding in the block came from, and it would have given the block two nested
// scroll contexts once the content started scrolling sideways.
//
// Highlights during render rather than calling hljs.highlightAll() from an effect.
// That version walked the whole document on every block's mount (O(n^2) down a long
// thread) and stamped data-highlighted on the nodes it touched, so a streamed block
// whose text changed afterwards kept the marker and was never re-highlighted — which
// is why some blocks in one answer came out coloured and others plain. No stylesheet
// is imported either; the `.hljs-*` colours are token-built in index.css.
const CodeBlock = ({ language, code = '' }) => {
  // `lang` comes back out so the class names the grammar the content was actually
  // highlighted with, not the fence's unrecognised label.
  //
  // Not wrapped in a try/catch: with hljs 11.11.1's defaults the only throw is
  // `Unknown language`, which getLanguage already guards. Illegal lexemes cannot
  // throw — `highlight()` defaults ignoreIllegals to true (core.js:1705, upstream
  // #3149), and `_highlight` catches both the Illegal path and, under the
  // on-by-default SAFE_MODE (core.js:1612), every other error, returning escaped
  // text instead (core.js:2164-2191).
  const { html, lang } = useMemo(() => {
    const effective = language && hljs.getLanguage(language) ? language : 'plaintext'
    return { html: hljs.highlight(code, { language: effective }).value, lang: effective }
  }, [language, code])

  // hljs.highlight escapes its input, so the only markup here is its own spans.
  return (
    <code className={`hljs language-${lang}`} dangerouslySetInnerHTML={{ __html: html }} />
  )
}

CodeBlock.propTypes = {
  language: PropTypes.string,
  code: PropTypes.string,
}

export default CodeBlock
